import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  Handle,
  Position,
  useUpdateNodeInternals,
  useReactFlow,
} from '@xyflow/react';

import {
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  CardHeader,
  Typography,
  Box,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Close, Search } from '@mui/icons-material';

// import SourceIcon from '../images/noun-open.png';
import BankIcon from '../images/bank.png';
import CreditCardIcon from '../images/creditcard.png';
import LoanIcon from '../images/loan.png';
import FinanceIcon from '../images/finance-icon.png';
import { theme } from '../../style/theme';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setNodeHeight } from '../../state/slices/mappingSlice';
import RefreshIcon from '@mui/icons-material/Refresh';
// import { fetchResolvedFields } from '../../state/slices/mappingSlice';
import { CONNECTOR_THEMES } from './connectorThemes';
import { resolveFieldsService } from '../../apis/EntitiesSelection';

const buildEntityLeavesMap = (nodes = []) => {
  // returns { entityId: [leafId, leafId …] }
  const map = {};
  const walk = (n) => {
    if (n.children?.length) {
      const leaves = [];
      const collectLeaves = (c) => {
        if (c.children?.length) c.children.forEach(collectLeaves);
        else leaves.push(c.id);
      };
      n.children.forEach(collectLeaves);
      map[n.id] = leaves;
      n.children.forEach(walk);
    }
  };
  nodes.forEach(walk);
  return map;
};

const SourceNode = ({ id, data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const debouncedQuery = useDebounce(searchQuery, 500);
  const [hoverEntity, setHoverEntity] = useState(false);

  const ref = useRef();
  const dispatch = useDispatch();
  const [fieldRefreshLoading, setFieldRefreshLoading] = useState(false);

  const [openNodes, setOpenNodes] = useState(() => {
    const init = {};
    const walk = (nodes) => {
      nodes?.forEach((n) => {
        if (n.children) {
          init[n.id] = true;
          walk(n.children);
        }
      });
    };
    walk(data?.subFields);
    return init;
  });

  const { workflowName: workflowId } = useParams();

  useLayoutEffect(() => {
    if (ref.current) {
      dispatch(
        setNodeHeight({
          workflowId,
          nodeId: id,
          height: ref.current.offsetHeight,
        })
      );
    }
  }, [data.subFields, openNodes]);

  // const expandAllTrigger = useSelector(
  //   (state) => state.mapping?.expandAllTriggerByWorkflow?.[workflowName]
  // );

  const finalConfigPayload = useSelector(
    (state) => state.mapping.finalConfigPayloadByWorkflow?.[workflowId]
  );

  const expandAllTrigger = useSelector(
    (state) => state.mapping?.expandAllTriggerByWorkflow?.[workflowId]
  );

const AUTO_COLLAPSE_MS = 100;
const didAutoCollapseRef = useRef(false);
const firstRenderRef = useRef(true);


  const updateNodeInternals = useUpdateNodeInternals();
  // eslint-disable-next-line
  const { setEdges, getEdges } = useReactFlow();

  // const connectedFieldIds = useMemo(() => {
  //   return getEdges().filter(e => e.source === id).map(e => e.sourceHandle);
  // }, [getEdges(), id]);

  const connectedFieldIds = getEdges()
    .filter((e) => e.source === id)
    .map((e) => e.sourceHandle);

  // console.log('connectedFields:', connectedFieldIds);

  useEffect(() => {
    requestAnimationFrame(() => updateNodeInternals(id));
  }, [connectedFieldIds.length, JSON.stringify(data.subFields), openNodes]);

  const entityLeavesMap = useMemo(
    () => buildEntityLeavesMap(data?.subFields),
    [data?.subFields]
  );

  const rerouteEdgesToEntity = (entityId) => {
    const leafIds = entityLeavesMap[entityId] || [];
    if (!leafIds.length) return;

    setEdges((eds) =>
      eds.map((e) => {
        if (e.source === id && leafIds.includes(e.sourceHandle)) {
          return {
            ...e,
            sourceHandle: entityId,
            data: {
              ...e.data,
              _originalSourceHandle: e.sourceHandle, 
              _isRerouted: true,
            },
          };
        }
        return e;
      })
    );
  };

  const restoreEdgesFromEntity = (entityId) => {
    setEdges((eds) =>
      eds.map((e) => {
        if (
          e.source === id &&
          e.sourceHandle === entityId &&
          e.data?._isRerouted &&
          e.data?._originalSourceHandle
        ) {
          // eslint-disable-next-line no-unused-vars
          const { _originalSourceHandle, _isRerouted, ...restData } = e.data;
          return {
            ...e,
            sourceHandle: _originalSourceHandle,
            data: restData,
          };
        }
        return e;
      })
    );
  };

  const toggleNode = (entityId) => {
    const willCollapse = openNodes[entityId];
    if (willCollapse) rerouteEdgesToEntity(entityId);
    else restoreEdgesFromEntity(entityId);

    setOpenNodes((p) => ({ ...p, [entityId]: !p[entityId] }));
    requestAnimationFrame(() => updateNodeInternals(id));
  };

useEffect(() => {
  if (didAutoCollapseRef.current) return;

  const init = {};
  const walk = (nodes) => {
    nodes?.forEach((n) => {
      if (n.children?.length) {
        init[n.id] = true;
        walk(n.children);
      }
    });
  };
  walk(data?.subFields);
  setOpenNodes(init);

  const t = setTimeout(() => {
    setOpenNodes((prev) => {
      const next = { ...prev };
      data?.subFields?.forEach((n) => {
        if (n.children?.length) {
          next[n.id] = false;
          rerouteEdgesToEntity(n.id);
        }
      });
      return next;
    });
    didAutoCollapseRef.current = true;
    requestAnimationFrame(() => updateNodeInternals(id));
  }, AUTO_COLLAPSE_MS);

  return () => clearTimeout(t);
}, [data?.subFields, id]);

useEffect(() => {
  if (firstRenderRef.current) { firstRenderRef.current = false; return; }
  if (!expandAllTrigger) return;

  const allOpen = {};
  const walk = (nodes) => {
    nodes?.forEach((n) => {
      if (n.children?.length) {
        allOpen[n.id] = true;
        walk(n.children);
      }
    });
  };
  walk(data?.subFields);
  setOpenNodes(allOpen);
  Object.keys(entityLeavesMap).forEach(restoreEdgesFromEntity);
  requestAnimationFrame(() => updateNodeInternals(id));
}, [expandAllTrigger]);


  const filterTree = (nodes, q) => {
    if (!q) return nodes;
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const connectedSet = new Set(connectedFieldIds);

    const filterNode = (n) => {
      const labelMatches =
        words.length === 0 || words.every((w) => n.label.toLowerCase().includes(w));
      const isConnected = connectedSet.has(n.id);

      if (n.children) {
        const kids = n.children.map(filterNode).filter(Boolean);
        return labelMatches || isConnected || kids.length
          ? { ...n, children: kids }
          : null;
      }

      return labelMatches || isConnected ? n : null;
    };

    return nodes.map(filterNode).filter(Boolean);
  };

const getFieldStatus = (fieldId, side) => {
  const edges = getEdges().filter(e =>
    side === 'source' ? e.sourceHandle === fieldId : e.targetHandle === fieldId
  );

  const hasManual = edges.some(e => e.data?.isManual);
  const hasFuzzyValid = edges.some(e => e.data?.isFuzzy && e.data?.isValid);
  const hasInvalid = edges.some(e => e.data?.isValid === false);

  return { hasManual, hasFuzzyValid, hasInvalid, isConnected: edges.length > 0 };
};


  const renderTree = (nodes, lvl = 0) => (
    <List component="div" disablePadding>
      {nodes?.map((n, idx) => {
        const isBranch = !!n.children;

        console.log('node::::', n);
        let children = n.children || [];
        children = [
          ...children.filter((f) => connectedFieldIds.includes(f.id)),
          ...children.filter((f) => !connectedFieldIds.includes(f.id)),
        ];

        // console.log('n.color:', n);

        const isField = !isBranch;
        const isConnected = isField && connectedFieldIds.includes(n.id);

        const { hasFuzzyValid } =  getFieldStatus(n.id, 'source');

        // console.log('hasManual:', hasManual);
        // console.log('hasFuzzyValid:', hasFuzzyValid);
        // console.log('hasInvalid:', hasInvalid);

        // console.log('children block:', children)
        // console.log('isConnected:', isConnected)

        return (
          <div key={n.id}>
            <ListItemButton
              onClick={() => isBranch && toggleNode(n.id)}
              sx={{
                my: 1,
                '&:hover': { backgroundColor: 'transparent' },
                ...(isBranch && {
                  bgcolor: lvl === 0 ? '#E5EFFB' : '#F2F2F2',
                  border: lvl === 0 ? '1px solid #264FDA' : '1px solid #000000',
                  borderRadius: 1,
                  color: '#264FDA',
                  py: 0.5,
                  position: 'relative',
                }),
                ...(!isBranch && {
                  borderBottom:
                    idx === nodes.length - 1
                      ? 'none'
                      : '0.1px solid rgba(56,56,56,.2)',
                  pb: 0,
                  pt: 0.5,
                  pl: 2,
                }),
              }}
            >
              <ListItemText
                primary={
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: '100%',
                      gap: 6,
                      color: ['Bank', 'Credit Card', 'Loan'].includes(n.label)
                        ? '#000'
                        : undefined,
                    }}
                  >
                    {n.label === 'Finance' ? (
                      <img src={FinanceIcon} alt="Finance" width={24} />
                    ) : n.label === 'Bank' ? (
                      <img src={BankIcon} alt="Bank" width={24} />
                    ) : n.label === 'Credit Card' ? (
                      <img src={CreditCardIcon} alt="CC" width={24} />
                    ) : n.label === 'Loan' ? (
                      <img src={LoanIcon} alt="Loan" width={24} />
                    ) : null}
                    {/* <Tooltip
                      title={!isBranch && (n.type || 'unknown')}
                      arrow
                      placement="right"
                    >
                      <span>{n.label}</span>
                    </Tooltip> */}
                    <span
                      style={{
                        // color: n.isMapped
                        //   ? n.isMismatch
                        //     ? '#e60000'
                        //     : '#006400'
                        //   : 'black',
                        // fontWeight: n.isMapped ? '550' : 'normal',
                        color:
                          (hasFuzzyValid === true && isConnected) || (n.color === true && isConnected)
                            ? '#006400'
                            : n.isMismatch
                              ? '#e60000'
                            : 'black',
                        fontWeight: (hasFuzzyValid === true && isConnected) || (n.color === true && isConnected) ? '550' : 'normal',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        maxWidth: 300,
                        display: 'inline-block',
                      }}
                    >
                      {n.label}
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: 'normal',
                          color: '#636363',
                        }}
                      >
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        {n.type ? `${n.type}` : ''}
                      </span>
                    </span>
                  </span>
                }
              />

              <ListItemIcon sx={{ minWidth: 20 }}>
                {isBranch ? (
                  openNodes[n.id] ? (
                    <RemoveCircleOutlineIcon
                      sx={{
                        color: ['Bank', 'Credit Card', 'Loan'].includes(n.label)
                          ? '#A0A0A0'
                          : '#264FDA',
                        ml: 1,
                      }}
                    />
                  ) : (
                    <ArrowDropDownCircleOutlinedIcon
                      sx={{
                        color: ['Bank', 'Credit Card', 'Loan'].includes(n.label)
                          ? '#A0A0A0'
                          : '#264FDA',
                        ml: 1,
                      }}
                    />
                  )
                ) : (
                  <span style={{ width: 8, height: 8 }} />
                )}
              </ListItemIcon>

              {isBranch && (
                <Handle
                  type="source"
                  position={Position.Right}
                  id={n.id}
                  onMouseEnter={() => setHoverEntity(true)}
                  onMouseLeave={() => setHoverEntity(false)}
                  style={{
                    background: hoverEntity ? 'transparent' : '#E5EFFB',
                    border: 'none',
                    boxShadow: 'none',
                    opacity: hoverEntity ? 1 : 0,
                    // right: '12%',
                    right: '0',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    position: 'absolute',
                    // display: openNodes[n.id] ? 'none' : 'block',
                    display: 'block',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                />
              )}

              {!isBranch && (
                <Handle
                  type="source"
                  position={Position.Right}
                  id={n.id}
                  style={{
                    background: theme.palette.primary.main,
                    right: '12%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    position: 'absolute',
                  }}
                />
              )}
            </ListItemButton>

            {isBranch && (
              <Collapse in={!!openNodes[n.id]} timeout="auto">
                {renderTree(children, lvl + 1)}
              </Collapse>
            )}
          </div>
        );
      })}
    </List>
  );

  // const handleNodeFieldRefresh = () => {
  //   if (!workflowName || !finalConfigPayload?.length) return;
  //   dispatch(fetchResolvedFields({
  //     label: 'FieldResolution',
  //     workflowName,
  //     finalConfigPayload,
  //     force: true,
  //   }));
  // };

  const getOnlySourceEntities = (configList = []) =>
    configList.filter((item) => item.type === 'source');

  // console.log('getOnlySourceEntities', getOnlySourceEntities(finalConfigPayload));
  const handleNodeFieldRefresh = async () => {
    if (!workflowId || !finalConfigPayload?.length) return;

    const sourceOnlyPayload = getOnlySourceEntities(finalConfigPayload);

    try {
      setFieldRefreshLoading(true); // Start loader

      const response = await resolveFieldsService({
        label: 'FieldResolution',
        configs: sourceOnlyPayload,
        force: true,
      });

      console.log('Resolved Fields Response:', response);
      // Optionally: dispatch(setResolvedFields(response));
      //     dispatch(setResolvedFields({
      //   workflowName,
      //   resolvedFields: response,
      // }));
    } catch (error) {
      console.error('Error resolving fields:', error);
    } finally {
      setFieldRefreshLoading(false);
    }
  };

  const nodeConfig = finalConfigPayload?.find((cfg) => cfg.type === data.type);
  const labelPrefix = nodeConfig?.label?.split('(')[0].trim().toLowerCase();
  const connectorTheme =
    CONNECTOR_THEMES[labelPrefix] || CONNECTOR_THEMES.default;

  return (
    <Card
      sx={{
        width: 400,
        border: '1px solid #C7C7C7',
        borderRadius: 2,
        position: 'relative',
        zIndex: -1,
      }}
      ref={ref}
    >
      <CardHeader
        // avatar={<img src={SourceIcon} alt="source" width={26} />}
        // avatar={
        //   connectorTheme.logo && (
        //     <img
        //       src={connectorTheme.logo}
        //       alt={connectorTheme.label}
        //       width={26}
        //     />
        //   )
        // }
        title={
          <Typography variant="subtitle1" fontWeight={500}>
            Source - {connectorTheme.label}
          </Typography>
        }
        action={
          <>
            <Tooltip title="Reload field data">
              <span>
                <IconButton
                  size="small"
                  onClick={handleNodeFieldRefresh}
                  disabled={fieldRefreshLoading}
                  aria-label="Reload field data"
                  sx={{ color: '#fff' }}
                >
                  {fieldRefreshLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              sx={{ color: '#fff' }}
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search />
            </IconButton>
          </>
        }
        sx={{
          // background: connectorTheme.gradient,
          background:
            '#5965A6',
          color: '#fff',
          px: 2,
          py: 1,
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.3)',
        }}
      />

      <CardContent sx={{ pt: 1 }}>
        <Collapse in={showSearch} timeout="auto" unmountOnExit>
          <Box>
            <TextField
              placeholder="Search fields…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: '#fff',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': { fontSize: 16, py: 0.5 },
              }}
              InputProps={{
                endAdornment: searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{
                      backgroundColor: '#fff',
                      color: theme.palette.error.main,
                      width: '1.25rem',
                      height: '1.25rem',
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: theme.palette.error.dark,
                        color: '#fff',
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Collapse>

        {renderTree(filterTree(data.subFields, debouncedQuery))}
      </CardContent>
    </Card>
  );
};

SourceNode.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    subFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string,
        children: PropTypes.array,
      })
    ).isRequired,
  }).isRequired,
};

export default SourceNode;
